import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { simulateLocalNotification, getFCMToken, onForegroundMessage, initializeFirebase } from '../services/firebase';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { apiService } from '../services/api';
import { ROLES } from '../utils/roles';

// Types
export interface Notification {
    id: string;
    type: 'booking' | 'info' | 'warning' | 'success';
    title: string;
    message: string;
    timestamp: Date;
    read: boolean;
    bookingId?: number;
    staffId?: number;
}

export interface Booking {
    id: number;
    customerName: string;
    service: string;
    staffId?: number;
    staffName?: string;
    date: string;
    time: string;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    fcmToken: string | null;
    permissionStatus: NotificationPermission | 'unsupported';
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearNotifications: () => void;
    requestPermission: () => Promise<boolean>;
    notifyNewBooking: (booking: Booking) => void;
    refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

const FCM_TOKEN_KEY = 'fcm_token';

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const { socket, isConnected } = useWebSocket();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [fcmToken, setFcmToken] = useState<string | null>(null);
    const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | 'unsupported'>('default');

    // Fetch notifications from backend
    const refreshNotifications = useCallback(async () => {
        if (!user) return;

        try {
            const response = await apiService.getNotifications();
            if (response.success && response.data) {
                const formatted = response.data.map((n: any) => ({
                    id: n.id,
                    type: n.type.includes('booking') ? 'booking' : 'info',
                    title: n.title,
                    message: n.message,
                    timestamp: new Date(n.createdAt),
                    read: n.read,
                    bookingId: n.data?.bookingId,
                    staffId: n.data?.employeeId
                }));
                setNotifications(formatted);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [user]);

    // Load notifications on mount and when user changes
    // Also poll every 15s (WebSocket disabled on Vercel serverless)
    useEffect(() => {
        if (!user) {
            setNotifications([]);
            return;
        }

        refreshNotifications();

        // Poll every 15 seconds to catch new bookings (fallback for WebSocket)
        const pollInterval = setInterval(async () => {
            try {
                const response = await apiService.getNotifications();
                if (response.success && response.data) {
                    const formatted = response.data.map((n: any) => ({
                        id: n.id,
                        type: n.type.includes('booking') ? 'booking' : 'info',
                        title: n.title,
                        message: n.message,
                        timestamp: new Date(n.createdAt),
                        read: n.read,
                        bookingId: n.data?.bookingId,
                        staffId: n.data?.employeeId
                    }));

                    setNotifications(prev => {
                        // Check if there are truly new unread notifications
                        const prevIds = new Set(prev.map(n => n.id));
                        const newOnes = formatted.filter((n: any) => !prevIds.has(n.id) && !n.read);

                        // Show browser notification for each new one (if permission granted)
                        if (newOnes.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
                            newOnes.forEach((n: any) => {
                                new Notification(n.title, {
                                    body: n.message,
                                    icon: '/logo.png',
                                    badge: '/badge.png'
                                });
                            });
                        }

                        return formatted;
                    });
                }
            } catch (err) {
                // Silent fail - don't spam console
            }
        }, 15000); // every 15 seconds

        return () => clearInterval(pollInterval);
    }, [user, refreshNotifications]);

    // Check notification permission status
    useEffect(() => {
        if ('Notification' in window) {
            setPermissionStatus(Notification.permission);
        } else {
            setPermissionStatus('unsupported');
        }

        // Load stored FCM token
        const storedToken = localStorage.getItem(FCM_TOKEN_KEY);
        if (storedToken) {
            setFcmToken(storedToken);
        }
    }, []);

    // Initialize Firebase and listen for foreground messages
    useEffect(() => {
        initializeFirebase();

        const unsubscribe = onForegroundMessage((payload) => {
            // Add notification from FCM message
            addNotification({
                type: 'booking',
                title: payload.notification?.title || 'New Notification',
                message: payload.notification?.body || '',
                bookingId: payload.data?.bookingId ? parseInt(payload.data.bookingId) : undefined
            });

            // Refresh from backend to sync
            refreshNotifications();
        });

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [refreshNotifications]);

    // Listen to WebSocket events
    useEffect(() => {
        if (!socket || !isConnected) return;

        // New booking event
        socket.on('booking:new', (data: any) => {
            addNotification({
                type: 'booking',
                title: 'Booking mới! 📅',
                message: `Khách: ${data.customerName}\nDịch vụ: ${data.serviceType}\nThời gian: ${data.bookingDate} ${data.time}`,
                bookingId: data.id
            });
            refreshNotifications();
        });

        // Booking assigned to staff
        socket.on('booking:assigned', (data: any) => {
            addNotification({
                type: 'booking',
                title: 'Booking được phân công! 👤',
                message: `Bạn được phân công phục vụ:\nKhách: ${data.customerName}\nThời gian: ${data.bookingDate} ${data.time}`,
                bookingId: data.id,
                staffId: data.employeeId
            });
            refreshNotifications();
        });

        // Booking status change
        socket.on('booking:statusChange', (data: any) => {
            addNotification({
                type: 'info',
                title: 'Booking cập nhật',
                message: `Booking đã chuyển sang trạng thái: ${data.status}`,
                bookingId: data.bookingId
            });
            refreshNotifications();
        });

        return () => {
            socket.off('booking:new');
            socket.off('booking:assigned');
            socket.off('booking:statusChange');
        };
    }, [socket, isConnected, refreshNotifications]);

    // Calculate unread count
    const unreadCount = notifications.filter(n => !n.read).length;

    // Add a new notification (local only)
    const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
        const newNotification: Notification = {
            ...notification,
            id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
            read: false
        };

        setNotifications(prev => [newNotification, ...prev].slice(0, 50));
    }, []);

    // Mark a notification as read
    const markAsRead = useCallback(async (id: string) => {
        try {
            await apiService.markNotificationAsRead(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, read: true } : n)
            );
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        try {
            await apiService.markAllNotificationsAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    }, []);

    // Clear all notifications
    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    // Request notification permission
    const requestPermission = useCallback(async (): Promise<boolean> => {
        if (!('Notification' in window)) {
            console.warn('Notifications not supported');
            setPermissionStatus('unsupported');
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            setPermissionStatus(permission);

            if (permission === 'granted') {
                // Try to get FCM token
                const token = await getFCMToken();
                if (token) {
                    setFcmToken(token);
                    localStorage.setItem(FCM_TOKEN_KEY, token);

                    // Register token with backend
                    // Now calling the real API
                    await apiService.registerFCMToken(token, 'web', navigator.userAgent);
                    console.log('✅ FCM token registered with backend');
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }, [user]);

    // Notify new booking based on role (kept for backward compatibility)
    const notifyNewBooking = useCallback((booking: Booking) => {
        if (!user) return;

        const shouldNotify = (): boolean => {
            if (user.role === ROLES.OWNER || user.role === ROLES.MANAGER || user.role === ROLES.ADMIN || user.role === ROLES.RECEPTIONIST) {
                return true;
            }
            if (user.role === ROLES.STAFF) {
                return booking.staffId === user.id;
            }
            return false;
        };

        if (shouldNotify()) {
            const title = 'Booking mới! 📅';
            const message = `Khách: ${booking.customerName}\nDịch vụ: ${booking.service}\nThời gian: ${booking.date} ${booking.time}`;

            addNotification({
                type: 'booking',
                title,
                message,
                bookingId: booking.id,
                staffId: booking.staffId
            });

            if (permissionStatus === 'granted') {
                simulateLocalNotification(title, message);
            }
        }
    }, [user, permissionStatus, addNotification]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            fcmToken,
            permissionStatus,
            addNotification,
            markAsRead,
            markAllAsRead,
            clearNotifications,
            requestPermission,
            notifyNewBooking,
            refreshNotifications
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
